import { ChangeEvent, ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Modal, Spinner } from 'react-bootstrap'
import { KTIcon } from '../../../../../_metronic/helpers'
import { AppSelect } from '../../../../../components/Select'
import { getPaymentMethodLookup } from '../../../../common/core/_requests'
import { getErrorCode, getErrorResponse } from '../../../../common/api/errors'
import { ApiResponse } from '../../../../common/api/response.types'
import { CopyableCell } from '../../../../common/CopyableCell'
import { ClientValue } from '../../../../common/ClientCell'
import { FileDownloadButton } from '../../../../../components/FileDownloadButton/FileDownloadButton'
import { IsGranted, useAuth } from '../../../auth'
import { MatchingFile, matchingCapabilities } from '../../../matching/core/_models'
import { BillStatusBadge } from '../../../matching/components/common/BillStatusBadge'
import { ReceiptUploadButton } from '../../../matching/components/common/ReceiptUploadButton'
import { useReceiptActions } from '../../../matching/components/common/useReceiptActions'
import { ConciergeRoutingChain, ConciergeSummary } from '../../core/_conciergeModels'
import {
  cancelConciergeIssuance,
  expireConcierge,
  extendConcierge,
  getConcierge,
  getConciergeRouting,
  lateCreditConcierge,
  searchConcierge,
  selectConciergeVariant,
  updateConcierge,
} from '../../core/_conciergeRequests'
import {
  conciergeCopyValue,
  conciergeHistoryText,
  conciergeIssuanceLines,
  conciergeMethodOptions,
  conciergeStage,
  formatMsk,
  lateCreditText,
  routingExclusions,
} from './conciergeView'

const POLL_MS = 2000
const SEARCHING = ['ADAPTER_PENDING', 'WAITING_FOR_REQUISITE']
const AMOUNT_RE = /^\d{0,9}([.,]\d{0,2})?$/
const MINUTES_RE = /^\d{0,2}$/
/** Vertical gap the dialog keeps from the window edges (Bootstrap's modal margin, both sides). */
const MODAL_GAP = 2 * 28
/** Height the inline history may take; longer history scrolls inside it. */
const HISTORY_MAX = 180
const HISTORY_TITLE = 32

type MethodOption = { value: string; label: string }

const errorText = (e: unknown): string =>
  getErrorResponse<{ general_error?: string }>(e)?.general_error ?? getErrorCode(e) ?? 'Не удалось выполнить действие'

const toNumber = (value: string): number => Number(value.replace(',', '.'))

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <>
    <dt className='col-sm-4 col-5 text-muted fw-normal mb-2'>{label}</dt>
    <dd className='col-sm-8 col-7 mb-2'>{children}</dd>
  </>
)

const HistoryTable = ({ items }: { items: ConciergeSummary['history'] }) => (
  <table className='table table-sm table-row-dashed fs-7 mb-0'>
    <tbody>
      {items.map((h, i) => (
        <tr key={i}>
          <td className='text-nowrap text-muted ps-0'>{formatMsk(h.at)}</td>
          <td>{conciergeHistoryText(h)}</td>
          <td className='text-end text-muted pe-0'>{h.user ?? ''}</td>
        </tr>
      ))}
    </tbody>
  </table>
)

/**
 * The deposit's receipt, worked the way the matching deposit row works it: download and decide when
 * there is one, attach one while the deposit awaits payment. The window re-reads the ticket after each
 * action, since a decision moves it.
 */
const ReceiptField = ({
  token,
  files,
  canAttach,
  canManage,
  onChanged,
}: {
  token: string
  files: MatchingFile[]
  canAttach: boolean
  canManage: boolean
  onChanged: () => void
}) => {
  const { fileInputRef, isUploading, handleUploadClick, handleFileSelect, handleVerifyBill, handleRejectBill } = useReceiptActions({
    ticketToken: token,
    canManageBills: canManage,
    onUploaded: onChanged,
    onDecided: onChanged,
  })

  if (files.length > 0) {
    return (
      <div className='d-flex flex-column gap-2' data-testid='concierge-receipts'>
        {files.map((file) => (
          <div key={file.id} className='d-flex align-items-center gap-2'>
            <FileDownloadButton url={file.url} id={file.id} status={file.status} badgeStatus={file.autoCheckStatus} badgekMessage={file.autoCheckMessage} />
            <BillStatusBadge
              bill={file}
              handleVerifyBill={() => handleVerifyBill(file)}
              handleRejectBill={() => handleRejectBill(file)}
              canManageBills={canManage}
            />
          </div>
        ))}
      </div>
    )
  }

  return canAttach && canManage ? (
    <ReceiptUploadButton fileInputRef={fileInputRef} isUploading={isUploading} onClick={handleUploadClick} onFileSelect={handleFileSelect} />
  ) : (
    <>—</>
  )
}

/**
 * The staff window of a concierge deposit: one window whose body follows the ticket's state —
 * method and amount before the search, the search itself, the tiles, the issued requisite, and the
 * summary with the history at all times. The step's actions live in the footer.
 */
const ConciergeModal = ({ token, show, onClose }: { token: string; show: boolean; onClose: () => void }) => {
  const [summary, setSummary] = useState<ConciergeSummary | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [method, setMethod] = useState<string | null>(null)
  const [amount, setAmount] = useState('')
  const [lookup, setLookup] = useState<string[] | null>(null)
  const [chain, setChain] = useState<ConciergeRoutingChain | null | undefined>(undefined)
  const [excluded, setExcluded] = useState<number[]>([])
  const [minutes, setMinutes] = useState('5')
  const [lateAmount, setLateAmount] = useState('')
  const [copied, setCopied] = useState(false)
  const { currentUser } = useAuth()
  const canManageBills = IsGranted(matchingCapabilities.canEditTickets, currentUser)
  const poll = useRef<ReturnType<typeof setTimeout> | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [historyFits, setHistoryFits] = useState(true)
  const [historyOpen, setHistoryOpen] = useState(false)

  const apply = useCallback((response: ApiResponse<ConciergeSummary>) => {
    if (response.success) {
      setSummary(response.data)
      setError(null)
    } else {
      setError(response.errors.general_error ?? 'Не удалось выполнить действие')
    }
  }, [])

  const run = async (key: string, action: () => Promise<ApiResponse<ConciergeSummary>>) => {
    setBusy(key)
    try {
      apply(await action())
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(null)
    }
  }

  const load = useCallback(async () => {
    try {
      apply(await getConcierge(token))
    } catch (e) {
      setError(errorText(e))
    }
  }, [apply, token])

  useEffect(() => {
    if (show) {
      load()
    }
  }, [show, load])

  // The shared payment method lookup: only enabled methods offered in the lists, under their own names.
  useEffect(() => {
    if (!show) return
    getPaymentMethodLookup()
      .then((response) => setLookup(response.data.success ? response.data.data.map((m) => m.label) : []))
      .catch(() => setLookup([]))
  }, [show])

  // The search is asynchronous: while it runs, re-read the summary until the ticket leaves it.
  useEffect(() => {
    if (!show || !summary || !SEARCHING.includes(summary.status)) {
      return
    }
    poll.current = setTimeout(load, POLL_MS)

    return () => {
      if (poll.current) clearTimeout(poll.current)
    }
  }, [show, summary, load])

  useEffect(() => {
    if (summary) {
      setMethod(summary.paymentMethod)
      setAmount(summary.requestedAmount !== null ? String(summary.requestedAmount) : '')
    }
  }, [summary?.status, summary?.paymentMethod, summary?.requestedAmount]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadRouting = useCallback(
    async (forMethod: string) => {
      setBusy('routing')
      try {
        const response = await getConciergeRouting(token, forMethod)
        const loaded = response.success ? response.data.chain : null
        setChain(loaded)
        setExcluded(routingExclusions(loaded, summary?.excludedAdapterIds ?? []))
      } catch (e) {
        setError(errorText(e))
      } finally {
        setBusy(null)
      }
    },
    [token, summary?.excludedAdapterIds]
  )

  // The cascade is what the search will walk: shown once both the method and the amount are saved.
  const savedMethod =
    summary?.status === 'MANUAL_REQUISITE' && summary.requestedAmount !== null ? summary.paymentMethod : null
  useEffect(() => {
    if (show && savedMethod) {
      loadRouting(savedMethod)
    } else {
      setChain(undefined)
    }
  }, [show, savedMethod, summary?.requestedAmount]) // eslint-disable-line react-hooks/exhaustive-deps

  const methodOptions: MethodOption[] = useMemo(
    () => conciergeMethodOptions(lookup, summary?.allowedMethods ?? []),
    [lookup, summary?.allowedMethods]
  )

  // The window never outgrows the screen: the inline history keeps a fixed height, and when even
  // that does not fit next to the rest, it folds into a button that lays it over the body.
  const historyRows = summary?.history.length ?? 0
  useLayoutEffect(() => {
    const content = contentRef.current
    if (!show || !content) return

    const measure = () => {
      const dialog = content.closest('.modal-content')
      const body = content.closest('.modal-body')
      if (!dialog || !body) return
      const chrome = ['.modal-header', '.modal-footer'].reduce(
        (sum, sel) => sum + ((dialog.querySelector(sel) as HTMLElement | null)?.offsetHeight ?? 0),
        0
      )
      const style = window.getComputedStyle(body)
      const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)
      const available = window.innerHeight - MODAL_GAP - chrome - padding
      const needed = content.offsetHeight + HISTORY_TITLE + Math.min(HISTORY_MAX, historyRows * 34)
      setHistoryFits(needed <= available)
    }

    measure()
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
    observer?.observe(content)
    window.addEventListener('resize', measure)

    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [show, historyRows])

  if (!show) {
    return null
  }

  const stage = summary ? conciergeStage(summary.status) : null
  const late = summary?.lateCredit ?? null
  const paramsChanged =
    summary !== null &&
    ((method !== null && method !== summary.paymentMethod) || (amount !== '' && toNumber(amount) !== summary.requestedAmount))
  const selectable = chain ? chain.adapters.filter((a) => a.id !== null && a.enabled) : []
  const chosen = selectable.filter((a) => !excluded.includes(a.id as number))

  const methodDirty = summary !== null && method !== null && method !== summary.paymentMethod
  const amountDirty = summary !== null && amount !== '' && toNumber(amount) !== summary.requestedAmount
  const saveMethod = () => method && run('method', () => updateConcierge(token, { paymentMethod: method }))
  const saveAmount = () => run('amount', () => updateConcierge(token, { amount: toNumber(amount) }))

  // ✓ applies the field, ✕ puts back what is saved — the way single fields are edited elsewhere.
  const applyReset = (key: string, onApply: () => void, onReset: () => void) => (
    <>
      <button type='button' className='btn btn-sm btn-icon btn-light-success' title='Применить' aria-label={`${key}-apply`} disabled={busy !== null} onClick={onApply}>
        {busy === key ? <Spinner animation='border' size='sm' /> : <KTIcon iconName='check' className='fs-2' />}
      </button>
      <button type='button' className='btn btn-sm btn-icon btn-light-danger' title='Сбросить' aria-label={`${key}-reset`} disabled={busy !== null} onClick={onReset}>
        <KTIcon iconName='cross' className='fs-2' />
      </button>
    </>
  )

  const copy = async () => {
    if (!summary?.issuance) return
    await navigator.clipboard.writeText(conciergeCopyValue(summary.issuance))
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const button = (key: string, label: string, onClick: () => void, variant = 'primary', disabled = false, extra = '') => (
    <button
      type='button'
      className={`btn btn-sm btn-${variant} ${extra}`}
      disabled={busy !== null || disabled}
      onClick={onClick}
    >
      {busy === key ? (
        <>
          <Spinner animation='border' size='sm' className='me-2' />
          {label}…
        </>
      ) : (
        label
      )}
    </button>
  )

  const searchDisabled = !savedMethod || paramsChanged || !chain || chosen.length === 0

  return (
    // The receipt confirmations open over the window: without this its focus trap takes the focus
    // back from them, and Enter/Esc stop working there.
    <Modal show={show} onHide={onClose} size='lg' centered scrollable enforceFocus={false}>
      <Modal.Header>
        <Modal.Title>Консьерж</Modal.Title>
        <button type='button' className='btn btn-icon btn-sm btn-active-light-primary ms-2' aria-label='Закрыть' onClick={onClose}>
          <KTIcon iconName='cross' className='fs-1' />
        </button>
      </Modal.Header>
      <Modal.Body className='position-relative'>
        <div ref={contentRef}>
        {error && (
          <div className='alert alert-danger py-2' role='alert'>
            {error}
          </div>
        )}
        {!summary ? (
          <div className='d-flex align-items-center'>
            <Spinner animation='border' size='sm' className='me-2' /> Загружаем тикет…
          </div>
        ) : (
          <>
            <dl className='row mb-4' data-testid='concierge-summary'>
              <Field label='Тикет'>
                <CopyableCell value={summary.token} />
                {summary.chatUrl && (
                  <a className='ms-3 text-nowrap' href={summary.chatUrl} target='_blank' rel='noopener noreferrer'>
                    <KTIcon iconName='exit-right-corner' className='fs-5 me-1' />
                    Открыть чат в Verbox
                  </a>
                )}
              </Field>
              <Field label='Клиент'>
                <ClientValue externalId={summary.client?.externalId} token={summary.client?.token} paymentTicketToken={summary.token} />
              </Field>
              <Field label='Транзакция мерчанта'>
                <CopyableCell value={summary.merchantTransactionId} />
              </Field>
              <Field label='Состояние'>{summary.status}</Field>
              <Field label='Метод оплаты'>
                {stage === 'manual' ? (
                  <div className='d-flex align-items-center gap-2'>
                    <div className='flex-grow-1' style={{ maxWidth: 320 }}>
                      <AppSelect<MethodOption>
                        aria-label='concierge-method'
                        options={methodOptions}
                        value={methodOptions.find((o) => o.value === method) ?? null}
                        onChange={(o) => o && setMethod(o.value)}
                        menuPortalTarget={document.body}
                        menuPosition='fixed'
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                    </div>
                    {methodDirty && applyReset('method', saveMethod, () => setMethod(summary.paymentMethod))}
                  </div>
                ) : (
                  summary.paymentMethod ?? '—'
                )}
              </Field>
              <Field label={`Сумма, ${summary.currency}`}>
                {stage === 'manual' ? (
                  <div className='d-flex align-items-center gap-2'>
                    <input
                      type='text'
                      inputMode='decimal'
                      aria-label='concierge-amount'
                      className='form-control form-control-sm'
                      style={{ maxWidth: 140 }}
                      value={amount}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => AMOUNT_RE.test(e.target.value) && setAmount(e.target.value)}
                    />
                    {amountDirty &&
                      applyReset('amount', saveAmount, () =>
                        setAmount(summary.requestedAmount !== null ? String(summary.requestedAmount) : '')
                      )}
                  </div>
                ) : (
                  summary.requestedAmount ?? '—'
                )}
              </Field>
              <Field label='Срок до'>
                <div className='d-flex flex-wrap align-items-center gap-2'>
                  <span>{formatMsk(summary.deadlineAt)} (МСК)</span>
                  {stage !== 'final' && summary.maxExtendMinutes > 0 && (
                    <div className='d-flex align-items-center gap-2'>
                      <input
                        type='text'
                        inputMode='numeric'
                        aria-label='extend-minutes'
                        className='form-control form-control-sm'
                        style={{ width: 56 }}
                        value={minutes}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => MINUTES_RE.test(e.target.value) && setMinutes(e.target.value)}
                      />
                      {button(
                        'extend',
                        `Продлить, мин (до ${summary.maxExtendMinutes})`,
                        () => run('extend', () => extendConcierge(token, Number(minutes))),
                        'light-primary',
                        !minutes || Number(minutes) < 1 || Number(minutes) > summary.maxExtendMinutes
                      )}
                    </div>
                  )}
                </div>
              </Field>
              <Field label='Чек'>
                <ReceiptField
                  token={summary.token}
                  files={summary.files}
                  canAttach={stage === 'issued'}
                  canManage={canManageBills && !summary.disputeOpen}
                  onChanged={load}
                />
              </Field>
            </dl>

            {stage === 'waiting-client' && <div className='mb-4 text-muted'>Клиент ещё не открыл ссылку на оплату.</div>}

            {stage === 'manual' && (
              <div className='mb-4' data-testid='concierge-manual'>
                {summary.searchFailure && (
                  <div className='alert alert-warning py-2 mb-4' data-testid='concierge-search-failure'>
                    <div className='fw-semibold'>Прошлый поиск не нашёл реквизит</div>
                    <div className='fs-7'>
                      {summary.searchFailure.adapter}
                      {summary.searchFailure.error ? ` — ${summary.searchFailure.error}` : ''}
                    </div>
                    <div className='fs-7 text-muted'>Можно сменить метод, сумму или адаптеры и искать снова.</div>
                  </div>
                )}

                {!savedMethod && <div className='text-muted'>Укажите метод оплаты и сумму — покажется каскад.</div>}

                {savedMethod && (
                  <div data-testid='concierge-cascade'>
                    <div className='d-flex align-items-center mb-2'>
                      <strong className='me-3'>Каскад</strong>
                      {chain !== undefined &&
                        button('routing', 'Перепроверить', () => loadRouting(savedMethod), 'light', false, 'btn-active-light-primary')}
                    </div>
                    {paramsChanged && <div className='text-muted mb-2'>Примените изменения (✓) — каскад покажется для них.</div>}
                    {chain === undefined && busy === 'routing' && (
                      <div className='d-flex align-items-center text-muted'>
                        <Spinner animation='border' size='sm' className='me-2' /> Загружаем каскад…
                      </div>
                    )}
                    {chain === null && <div className='text-muted'>Для этого метода каскада нет.</div>}
                    {chain && (
                      <ul className='list-unstyled mb-0'>
                        {chain.adapters.map((a) => {
                          const available = a.id !== null && a.enabled
                          return (
                            <li key={`${a.priority}-${a.name}`} className='mb-1'>
                              <label className={`form-check form-check-sm form-check-custom ${available ? '' : 'opacity-50'}`}>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  disabled={!available || busy !== null}
                                  checked={available && !excluded.includes(a.id as number)}
                                  onChange={() =>
                                    setExcluded((prev) =>
                                      prev.includes(a.id as number) ? prev.filter((x) => x !== a.id) : [...prev, a.id as number]
                                    )
                                  }
                                />
                                <span className='form-check-label'>
                                  {a.priority}. {a.name}
                                  {!a.enabled && a.disabledReason ? ` — выключен (${a.disabledReason})` : ''}
                                </span>
                              </label>
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}

            {late !== null && (
              <div className='mb-4'>
                <strong>Оплата пришла на прежний реквизит</strong>
                <div className='text-muted mt-1'>{lateCreditText(late, summary.currency)}</div>
                {late.mode !== 'AWAITING_CONFIRMATION' && (
                  <div className='d-flex align-items-center gap-2 mt-2'>
                    {late.mode === 'AMOUNT' && (
                      <input
                        type='text'
                        inputMode='decimal'
                        aria-label='late-credit-amount'
                        className='form-control form-control-sm w-auto'
                        placeholder='Пришло, сумма'
                        value={lateAmount}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => AMOUNT_RE.test(e.target.value) && setLateAmount(e.target.value)}
                      />
                    )}
                    {button(
                      'late',
                      'Зачесть',
                      () => run('late', () => lateCreditConcierge(token, late.externalTransactionId, late.mode === 'AMOUNT' ? toNumber(lateAmount) : null)),
                      'light-warning',
                      late.mode === 'AMOUNT' && !lateAmount
                    )}
                  </div>
                )}
              </div>
            )}

            {stage === 'searching' && (
              <div className='mb-4 d-flex align-items-center'>
                <Spinner animation='border' size='sm' className='me-2' /> Ищем реквизит…
              </div>
            )}

            {stage === 'variants' && (
              <div className='mb-4'>
                <div className='mb-2'>Ровно под эту сумму выплаты нет. Выберите сумму для клиента:</div>
                <div className='d-flex flex-wrap gap-2'>
                  {summary.variants.map((v) =>
                    button(`variant-${v.id}`, `${v.amount} ${summary.currency}`, () => run(`variant-${v.id}`, () => selectConciergeVariant(token, v.id)), 'light-primary')
                  )}
                </div>
              </div>
            )}

            {summary.issuance && (stage === 'issued' || stage === 'receipt' || stage === 'variants') && (
              <div className='mb-4 border rounded p-3' data-testid='concierge-issuance'>
                <pre className='mb-3'>
                  {conciergeIssuanceLines(summary.issuance, summary.currency).map((line) => (
                    <div key={line.label}>
                      {line.label}:{' '}
                      {line.link ? (
                        <a href={line.value} target='_blank' rel='noopener noreferrer'>
                          {line.value}
                        </a>
                      ) : (
                        line.value
                      )}
                    </div>
                  ))}
                </pre>
                {button('copy', copied ? 'Скопировано' : 'Скопировать', copy, 'light-primary')}
              </div>
            )}

            {stage === 'receipt' && <div className='text-muted mb-4'>Чек на проверке: чтобы сменить реквизит, сначала отклоните чек.</div>}

          </>
        )}
        </div>

        {summary && summary.history.length > 0 &&
          (historyFits ? (
            <div className='mt-4' data-testid='concierge-history'>
              <strong>История</strong>
              <div className='mt-2' style={{ maxHeight: HISTORY_MAX, overflowY: 'auto' }}>
                <HistoryTable items={summary.history} />
              </div>
            </div>
          ) : (
            <button type='button' className='btn btn-sm btn-light mt-4' onClick={() => setHistoryOpen(true)}>
              История ({summary.history.length})
            </button>
          ))}

        {summary && historyOpen && !historyFits && (
          <div className='position-absolute top-0 start-0 w-100 h-100 bg-body p-6 overflow-auto' style={{ zIndex: 2 }} data-testid='concierge-history-overlay'>
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <strong>История</strong>
              <button type='button' className='btn btn-sm btn-light' onClick={() => setHistoryOpen(false)}>
                Скрыть
              </button>
            </div>
            <HistoryTable items={summary.history} />
          </div>
        )}
      </Modal.Body>
      {summary && stage !== 'final' && stage !== 'waiting-client' && stage !== 'receipt' && (
        <Modal.Footer className='justify-content-between'>
          <div>
            {stage === 'manual' && button('expire', 'Закрыть тикет', () => run('expire', () => expireConcierge(token)), 'light-danger')}
          </div>
          <div>
            {stage === 'manual' &&
              button('search', 'Найти реквизит', () => run('search', () => searchConcierge(token, excluded)), 'primary', searchDisabled)}
            {(stage === 'issued' || stage === 'variants' || stage === 'searching') &&
              button('cancel', 'Отменить выдачу', () => run('cancel', () => cancelConciergeIssuance(token)), 'light-danger', !summary.canCancelIssuance)}
          </div>
        </Modal.Footer>
      )}
    </Modal>
  )
}

export default ConciergeModal
